/**
 * 261030 Phase B3 / B3-5 — hộp «Sửa tuổi / đổi nhân vật» của bước ①: mỗi NHÂN VẬT một nhóm, các hình thái (tuổi / hình thái) của nhân vật đó là
 * lựa chọn LOẠI TRỪ + «Không có mặt». Chỉ logic thuần (không React / antd) để jest chạy được.
 * Nguồn: `status.step1.roster[]` (BE `filmWorkflowFourArtifact`): mỗi hàng `{ id, name, kind, ageOrForm, species, identityOf: { baseId, kind } | null }`.
 */
export interface PresenceForm { id: string; label: string; isBase: boolean }
export interface PresenceGroup {
  /** id nhân vật gốc (hoặc chính id nếu không có biến thể). */
  key: string;
  name: string;
  kind: string;
  forms: PresenceForm[];
  /** ≥ 2 hình thái ⇒ nhóm radio; 1 hình thái ⇒ checkbox như cũ. */
  multi: boolean;
}

const idOf = (entry: any): string => String(entry?.ownerId || entry?.id || '');
const labelOf = (entry: any): string => { const t = String(entry?.ageOrForm || entry?.species || entry?.role || '').trim(); return t.length > 60 ? `${t.slice(0, 59).trimEnd()}…` : t; };

/** Nhóm theo nhân vật (bỏ đạo cụ). Gốc trước, rồi theo thứ tự roster. Thứ tự nhóm = thứ tự xuất hiện đầu tiên trong roster. */
export function presenceGroupsOf(roster: any[] | null | undefined): PresenceGroup[] {
  const groups = new Map<string, PresenceGroup>();
  for (const entry of roster ?? []) {
    if (!entry || entry.kind === 'PROP') continue;
    const id = idOf(entry);
    if (!id) continue;
    const baseId = entry.identityOf?.baseId ? String(entry.identityOf.baseId) : id;
    const isBase = baseId === id;
    let group = groups.get(baseId);
    if (!group) { group = { key: baseId, name: String(entry.name ?? ''), kind: String(entry.kind || 'PERSON'), forms: [], multi: false }; groups.set(baseId, group); }
    if (isBase && entry.name) group.name = String(entry.name);
    group.forms.push({ id, label: labelOf(entry) || (isBase ? 'Bản chính' : 'Biến thể'), isBase });
  }
  const list = Array.from(groups.values());
  for (const g of list) { g.forms.sort((a, b) => Number(b.isBase) - Number(a.isBase)); g.multi = g.forms.length >= 2; }
  return list;
}

/**
 * B3-fix1 (smoke B3, 1c): lối mở hộp «Sửa tuổi / đổi nhân vật» KHÔNG phụ thuộc cờ — có nhân vật nhiều hình thái (≥ 2 tuổi/hình thái trong roster) ⇒ video nào cũng có nút sửa.
 * Trước đây hộp chỉ mở được từ cờ mang fix PRESENCE ⇒ plan không có cờ đó thì người không sửa được tuổi.
 */
export const hasMultiFormCharacter = (roster: any[] | null | undefined): boolean => presenceGroupsOf(roster).some((g) => g.multi);

/** Hình thái đang được chọn của nhóm (null = «Không có mặt»). Nếu (dữ liệu cũ) có nhiều hơn một ⇒ cái đầu theo thứ tự nhóm. */
export function presenceChoiceOf(group: PresenceGroup, selectedIds: string[]): string | null {
  const set = new Set(selectedIds);
  return group.forms.find((f) => set.has(f.id))?.id ?? null;
}

/** Áp lựa chọn của MỘT nhóm: bỏ mọi hình thái của nhóm đó rồi thêm `chosenId` (null ⇒ không có mặt). Id ngoài roster giữ nguyên. */
export function applyPresenceChoice(selectedIds: string[], group: PresenceGroup, chosenId: string | null): string[] {
  const own = new Set(group.forms.map((f) => f.id));
  const next = selectedIds.filter((id) => !own.has(id));
  if (chosenId && own.has(chosenId)) next.push(chosenId);
  return next;
}

export interface PresenceMerged { key: string; name: string; keptLabel: string; count: number }
/** Lúc mở hộp: video đang có ≥ 2 hình thái của cùng nhân vật (lỗi «hai Cael») ⇒ giữ MỘT (hình thái sau trong nhóm = cụ thể hơn) và báo để người đổi nếu sai. */
export function normalizePresenceIds(roster: any[] | null | undefined, ids: string[]): { ids: string[]; merged: PresenceMerged[] } {
  let next = ids.slice();
  const merged: PresenceMerged[] = [];
  for (const group of presenceGroupsOf(roster)) {
    if (!group.multi) continue;
    const current = next;
    const have = group.forms.filter((f) => current.includes(f.id));
    if (have.length < 2) continue;
    const keep = have[have.length - 1];
    next = applyPresenceChoice(next, group, keep.id);
    merged.push({ key: group.key, name: group.name, keptLabel: keep.label, count: have.length });
  }
  return { ids: next, merged };
}
